<template>
  <div class="w-full text-slate-600">

    <p class="text-base font-medium text-slate-900 pb-2">{{ questionTitle }}</p>
    <p v-if="canAffectEligibility" class="-mt-1.5 mb-4 text-xs text-slate-500 flex items-center gap-0.5">
      <InformationCircleIcon class="h-3.5 w-3.5 " aria-hidden="true"/>
      This answer could affect your eligibility for this position.
    </p>

    <Component
      v-if="answerComponent"
      :is="answerComponent.component"
      v-bind="answerComponent.props"
      @update:answer="(e) => updateAnswer(e.value, e.meta || {})"
    />

    <ScreeningQuestionAnswerTypeSwitcher
      v-if="canSwitchAnswer"
      :model-value="answerType"
      :answer="answer"
      :initial-type="props.question.type"
      @update:modelValue="changeAnswerType"
    />

  </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {
  SCREENING_QUESTION_TYPE_DATE,
  SCREENING_QUESTION_TYPE_MULTISELECT,
  SCREENING_QUESTION_TYPE_SELECT,
  SCREENING_QUESTION_TYPE_TEXT,
  SCREENING_QUESTION_TYPE_TEXT_OR_VIDEO,
  SCREENING_QUESTION_TYPE_TEXTAREA,
  SCREENING_QUESTION_TYPE_VIDEO
} from "../constants/ScreeningQuestions";
import {InformationCircleIcon} from "@heroicons/vue/24/outline";
import ScreeningQuestionAnswerText from "./ScreeningQuestionAnswerText.vue";
import ScreeningQuestionAnswerVideo from "./ScreeningQuestionAnswerVideo.vue";
import ScreeningQuestionAnswerTypeSwitcher from "./ScreeningQuestionAnswerTypeSwitcher.vue";
import ScreeningQuestionAnswerSelect from "./ScreeningQuestionAnswerSelect.vue";
import ScreeningQuestionAnswerDate from "./ScreeningQuestionAnswerDate.vue";

const emits = defineEmits(["update:answer"]);
const props = defineProps({
  question: Object,
  answer: Object,
  number: Number,
  total: Number,
});

const answerType = ref("");

const questionTitle = computed(() => {
  if (props.total > 4) {
    return `${props.number}. ${props.question.question}`;
  }

  return props.question.question;
});

const updateAnswer = (answer, meta = {}) => {
  emits("update:answer", {
    question: props.question.question,
    type: answerType?.value,
    answer,
    ...meta
  });
};

const componentMap = {
  [SCREENING_QUESTION_TYPE_TEXT]: {
    component: ScreeningQuestionAnswerText,
    props: {
      question: props.question,
      answer: props.answer,
      maxLength: 512,
      defaultHeight: 128,
      name: `text-answer-${props.number}`,
    },
    defaultAnswer: "",
    defaultMeta: {},
  },
  [SCREENING_QUESTION_TYPE_VIDEO]: {
    component: ScreeningQuestionAnswerVideo,
    props: {
      question: props.question,
      answer: props.answer,
      id: `video-answer-${props.number}`,
    },
    defaultAnswer: null,
    defaultMeta: {
      path: props.answer?.path || "",
    }
  },
  [SCREENING_QUESTION_TYPE_TEXTAREA]: {
    component: ScreeningQuestionAnswerText,
    props: {
      question: props.question,
      answer: props.answer,
      maxLength: 4096,
      defaultHeight: 256,
      name: `textarea-answer-${props.number}`,
    },
    defaultAnswer: "",
    defaultMeta: {},
  },
  [SCREENING_QUESTION_TYPE_SELECT]: {
    component: ScreeningQuestionAnswerSelect,
    props: {
      question: props.question,
      answer: props.answer,
      isMultiSelect: false,
      customNameSuffix: `answer-${props.number}`,
    },
    defaultAnswer: "",
    defaultMeta: {},
  },
  [SCREENING_QUESTION_TYPE_MULTISELECT]: {
    component: ScreeningQuestionAnswerSelect,
    props: {
      question: props.question,
      answer: props.answer,
      isMultiSelect: true,
      customNameSuffix: `answer-${props.number}`,
    },
    defaultAnswer: [],
    defaultMeta: {},
  },
  [SCREENING_QUESTION_TYPE_DATE]: {
    component: ScreeningQuestionAnswerDate,
    props: {
      question: props.question,
      answer: props.answer,
      name: `date-answer-${props.number}`,
    },
    defaultAnswer: "",
    defaultMeta: {},
  },
};

const setInitialAnswerType = () => {
  const currentAnswer = props.answer?.answer || null;

  if (props.question.type === SCREENING_QUESTION_TYPE_TEXT_OR_VIDEO) {

    if (typeof currentAnswer === "string" && currentAnswer.length) answerType.value = SCREENING_QUESTION_TYPE_TEXT;
    else answerType.value = SCREENING_QUESTION_TYPE_VIDEO;

  } else answerType.value = props.question.type;

  if (!currentAnswer) {
    const {defaultAnswer, defaultMeta} = componentMap[answerType.value];
    updateAnswer(defaultAnswer, defaultMeta);
  }
};

const answerComponent = computed(() => answerType.value ? componentMap[answerType.value] : null);

const canSwitchAnswer = computed(() => props.question.type.includes("_or_"));

const canAffectEligibility = computed(() => !!props.question.couldAffectEligibility);

const changeAnswerType = (newAnswerType) => {
  if (!canSwitchAnswer.value) return;
  answerType.value = newAnswerType;
  updateAnswer(componentMap[newAnswerType].defaultAnswer);
};

onMounted(() => setInitialAnswerType());
</script>
